import { webPaths } from "@zplanem/web-paths"
import type React from "react"
import { NavLink, useNavigate } from "react-router-dom"
import { useAuth } from "../auth-context"
import { useVisiblePropertyTabs } from "../hooks/use-visible-property-tabs"
import { getTranslations } from "../i18n"
import { useLocale } from "../locale-context"
import { MobileMoreMenuItem } from "./mobile-more-menu"
import { propertyTabPath } from "./property-tabs"
import { PROPERTY_TAB_ICONS } from "./tab-icons"

/**
 * Property-scoped mobile bottom bar (Android PropertyBottomBar parity).
 * Renders on property tab routes where MobileBottomNav yields. The More
 * menu keeps the global destinations (Properties / Profile / Sign out)
 * reachable.
 */
export const PropertyBottomNav: React.FC<{ propertyId: string }> = ({
	propertyId,
}) => {
	const { logout } = useAuth()
	const { locale } = useLocale()
	const t = getTranslations(locale)
	const navigate = useNavigate()
	const tabs = useVisiblePropertyTabs(propertyId)

	return (
		<nav
			aria-label={t.properties.tabs.navLabel}
			className="md:hidden fixed bottom-0 left-0 right-0 h-16 z-30 bg-card border-t border-border flex items-stretch"
		>
			{tabs.map((tab) => {
				const Icon = PROPERTY_TAB_ICONS[tab.key]
				return (
					<NavLink
						key={tab.key}
						to={propertyTabPath(propertyId, tab)}
						end={tab.path === ""}
						className={({ isActive }) =>
							"flex-1 flex flex-col items-center justify-center gap-1 border-t-2 text-xs transition-colors " +
							(isActive
								? "border-ring bg-accent/40 text-foreground"
								: "border-transparent text-muted-foreground hover:text-foreground")
						}
					>
						<Icon />
						<span>{t.properties.tabs[tab.key]}</span>
					</NavLink>
				)
			})}

			<MobileMoreMenuItem
				items={[
					{
						label: t.sidebar.properties,
						onSelect: () => navigate(webPaths.dashboard()),
					},
					{
						label: t.sidebar.profile,
						onSelect: () => navigate(webPaths.profile()),
					},
					{
						label: t.sidebar.settings,
						onSelect: () => navigate(webPaths.settings()),
					},
					{ label: t.sidebar.signOut, onSelect: logout, tone: "danger" },
				]}
			/>
		</nav>
	)
}
